export interface EncryptionResult {
  encryptedData: string;
  salt: string;
  iv: string;
}

export interface DecryptionOptions {
  encryptedData: string;
  password: string;
  salt?: string;
  iv?: string;
}

export class EncryptionService {
  private readonly ITERATIONS = 100000; // PBKDF2 iterations
  private readonly KEY_LENGTH = 256; // AES-256
  private readonly SALT_LENGTH = 16; // 128 bits
  private readonly IV_LENGTH = 12; // 96 bits for GCM

  /**
   * Encrypt data using AES-256-GCM with PBKDF2 key derivation
   */
  async encryptData(data: string, password: string): Promise<EncryptionResult> {
    if (!password || password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }

    try {
      const encoder = new TextEncoder();

      // Generate random salt and IV
      const salt = crypto.getRandomValues(new Uint8Array(this.SALT_LENGTH));
      const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

      // Import password as key material
      const keyMaterial = await crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
      );

      // Derive encryption key using PBKDF2
      const key = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt,
          iterations: this.ITERATIONS,
          hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: this.KEY_LENGTH },
        false,
        ['encrypt']
      );

      // Encrypt the data
      const encryptedData = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encoder.encode(data)
      );

      return {
        encryptedData: this.arrayBufferToBase64(encryptedData),
        salt: this.arrayBufferToBase64(salt.buffer),
        iv: this.arrayBufferToBase64(iv.buffer)
      };

    } catch (error) {
      throw new Error(`Encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Decrypt data using AES-256-GCM
   */
  async decryptData(options: DecryptionOptions): Promise<string> {
    const { encryptedData, password, salt, iv } = options;

    if (!password) {
      throw new Error('Password is required for decryption');
    }

    try {
      const encoder = new TextEncoder();
      const decoder = new TextDecoder();

      // Convert base64 strings back to ArrayBuffers
      const saltBuffer = salt ? this.base64ToArrayBuffer(salt) : new Uint8Array(this.SALT_LENGTH);
      const ivBuffer = iv ? this.base64ToArrayBuffer(iv) : new Uint8Array(this.IV_LENGTH);
      const encryptedBuffer = this.base64ToArrayBuffer(encryptedData);

      // Import password as key material
      const keyMaterial = await crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
      );

      // Derive decryption key using PBKDF2
      const key = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: saltBuffer,
          iterations: this.ITERATIONS,
          hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: this.KEY_LENGTH },
        false,
        ['decrypt']
      );

      // Decrypt the data
      const decryptedData = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: ivBuffer },
        key,
        encryptedBuffer
      );

      return decoder.decode(decryptedData);

    } catch (error) {
      if (error instanceof Error && error.name === 'OperationError') {
        throw new Error('Decryption failed: Invalid password or corrupted data');
      }
      throw new Error(`Decryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Encrypt data with combined format (salt + iv + encrypted data in one string)
   */
  async encryptDataCombined(data: string, password: string): Promise<string> {
    const result = await this.encryptData(data, password);

    // Combine salt, iv, and encrypted data into a single base64 string
    const saltBuffer = this.base64ToArrayBuffer(result.salt);
    const ivBuffer = this.base64ToArrayBuffer(result.iv);
    const encryptedBuffer = this.base64ToArrayBuffer(result.encryptedData);

    const combined = new Uint8Array(
      saltBuffer.byteLength + ivBuffer.byteLength + encryptedBuffer.byteLength
    );

    combined.set(new Uint8Array(saltBuffer), 0);
    combined.set(new Uint8Array(ivBuffer), saltBuffer.byteLength);
    combined.set(new Uint8Array(encryptedBuffer), saltBuffer.byteLength + ivBuffer.byteLength);

    return this.arrayBufferToBase64(combined.buffer);
  }

  /**
   * Decrypt data from combined format
   */
  async decryptDataCombined(combinedData: string, password: string): Promise<string> {
    try {
      const combined = new Uint8Array(this.base64ToArrayBuffer(combinedData));

      // Extract salt, iv, and encrypted data
      const salt = combined.slice(0, this.SALT_LENGTH);
      const iv = combined.slice(this.SALT_LENGTH, this.SALT_LENGTH + this.IV_LENGTH);
      const encryptedData = combined.slice(this.SALT_LENGTH + this.IV_LENGTH);

      return await this.decryptData({
        encryptedData: this.arrayBufferToBase64(encryptedData.buffer),
        password,
        salt: this.arrayBufferToBase64(salt.buffer),
        iv: this.arrayBufferToBase64(iv.buffer)
      });

    } catch (error) {
      throw new Error(`Combined decryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate a secure random password
   */
  generateSecurePassword(length: number = 16): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);

    return Array.from(array, byte => charset[byte % charset.length]).join('');
  }

  /**
   * Check password strength
   */
  checkPasswordStrength(password: string): {
    score: number; // 0-5
    feedback: string[];
    isStrong: boolean;
  } {
    const feedback: string[] = [];
    let score = 0;

    if (password.length < 8) {
      feedback.push('Password should be at least 8 characters long');
    } else if (password.length >= 12) {
      score += 1;
    }

    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('Add lowercase letters');

    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('Add uppercase letters');

    if (/[0-9]/.test(password)) score += 1;
    else feedback.push('Add numbers');

    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    else feedback.push('Add special characters (!@#$%^&*)');

    // Check for common patterns
    if (/(.)\1{2,}/.test(password)) {
      feedback.push('Avoid repeating characters');
      score = Math.max(0, score - 1);
    }

    if (/123|abc|qwe|password|admin/i.test(password)) {
      feedback.push('Avoid common patterns');
      score = Math.max(0, score - 1);
    }

    return {
      score,
      feedback,
      isStrong: score >= 4 && password.length >= 12
    };
  }

  /**
   * Hash data using SHA-256 (for checksums, not passwords)
   */
  async hashData(data: string): Promise<string> {
    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(data));
    return this.arrayBufferToBase64(hashBuffer);
  }

  /**
   * Verify data integrity using hash
   */
  async verifyDataIntegrity(data: string, expectedHash: string): Promise<boolean> {
    const actualHash = await this.hashData(data);
    return actualHash === expectedHash;
  }

  // Utility methods
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Check if Web Crypto API is available
   */
  isSupported(): boolean {
    return typeof crypto !== 'undefined' &&
           typeof crypto.subtle !== 'undefined' &&
           typeof crypto.getRandomValues === 'function';
  }

  /**
   * Get encryption capabilities info
   */
  getCapabilities(): {
    supported: boolean;
    algorithms: string[];
    keyLengths: number[];
  } {
    return {
      supported: this.isSupported(),
      algorithms: ['AES-GCM', 'PBKDF2', 'SHA-256'],
      keyLengths: [256]
    };
  }
}

// Create singleton instance
export const encryptionService = new EncryptionService();

// Expose debug utilities in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).encryptionDebug = {
    // Test encryption/decryption
    test: async (data: string = 'Hello, World!', password: string = 'test-password-123') => {
      console.log('🔐 Testing encryption/decryption...');
      try {
        const encrypted = await encryptionService.encryptDataCombined(data, password);
        console.log('✅ Encrypted:', encrypted.substring(0, 50) + '...');

        const decrypted = await encryptionService.decryptDataCombined(encrypted, password);
        console.log('✅ Decrypted:', decrypted);

        const isValid = decrypted === data;
        console.log('✅ Valid:', isValid);

        return { encrypted, decrypted, isValid };
      } catch (error) {
        console.error('❌ Test failed:', error);
        return { error };
      }
    },

    // Generate secure password
    generatePassword: (length?: number) => {
      const password = encryptionService.generateSecurePassword(length);
      console.log('🔑 Generated password:', password);
      return password;
    },

    // Check password strength
    checkPassword: (password: string) => {
      const result = encryptionService.checkPasswordStrength(password);
      console.log('💪 Password strength:', result);
      return result;
    },

    // Get capabilities
    capabilities: () => {
      const caps = encryptionService.getCapabilities();
      console.log('🛠️ Encryption capabilities:', caps);
      return caps;
    }
  };

  // console.log('🔧 Encryption Debug utilities available:');
  // console.log('  encryptionDebug.test()           - Test encryption/decryption');
  // console.log('  encryptionDebug.generatePassword() - Generate secure password');
  // console.log('  encryptionDebug.checkPassword()  - Check password strength');
  // console.log('  encryptionDebug.capabilities()   - Get encryption capabilities');
}